import type { Map as MapLibreMap } from "maplibre-gl";

import { TREAT_VARIANTS, treatIconId } from "../lib/spot-offer.ts";
import { ICON_VIEWPORT, spotIconDataUri } from "./spot-icon-svg.ts";

/** Rendu à 2× : net sur écran dense, et MapLibre redescend tout seul à 1×. */
const PIXEL_RATIO = 2;

export type SpotIconImages = ReadonlyMap<string, ImageData>;

/**
 * Rastérise les SVG des pastilles, une image par état de bonbons.
 * Volontairement sans référence à la carte : le chargement est asynchrone, et
 * une carte peut être détruite entre-temps. L'enregistrement, lui, est
 * synchrone (`registerSpotIcons`).
 */
export const loadSpotIcons = async (): Promise<SpotIconImages> => {
  const entries = await Promise.all(
    TREAT_VARIANTS.map(
      async (treats) => [treatIconId(treats), await rasterize(spotIconDataUri(treats))] as const,
    ),
  );

  return new Map(entries);
};

export const registerSpotIcons = (map: MapLibreMap, images: SpotIconImages): void => {
  for (const [id, image] of images) {
    if (!map.hasImage(id)) {
      map.addImage(id, image, { pixelRatio: PIXEL_RATIO });
    }
  }
};

const rasterize = async (dataUri: string): Promise<ImageData> => {
  const size = ICON_VIEWPORT * PIXEL_RATIO;
  const image = new Image(size, size);
  image.decoding = "async";
  image.src = dataUri;

  await image.decode();

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;

  const context = canvas.getContext("2d");

  if (context === null) {
    throw new Error("Contexte 2D indisponible : impossible de préparer les pastilles.");
  }

  context.drawImage(image, 0, 0, size, size);

  return context.getImageData(0, 0, size, size);
};
